// src/services/orderService.ts
import { 
    collection, 
    doc, 
    getDoc, 
    getDocs, 
    updateDoc,
    query,
    where,
    orderBy,
    limit as firestoreLimit,
    startAfter,
    type QueryConstraint,
    Timestamp
  } from 'firebase/firestore';
  import { db } from '../config/firebase';
  
  export interface Order {
    id: string;
    orderNumber: string;
    customer: {
      id: string;
      name: string;
      email: string;
      phone: string;
    };
    items: Array<{
      id: string;
      name: string;
      price: number;
      quantity: number;
    }>;
    status: 'pending' | 'processing' | 'completed' | 'cancelled';
    paymentStatus: 'pending' | 'paid' | 'failed';
    paymentMethod: string;
    subtotal: number;
    tax: number;
    deliveryFee: number;
    total: number;
    shippingAddress: {
      street: string;
      city: string;
      state: string;
      country: string;
      postalCode: string;
    };
    createdAt: Timestamp;
  updatedAt: Timestamp;
  }
  
  interface OrderQueryOptions {
    status?: string;
    customerId?: string;
    startAfterDoc?: any;
    limit?: number;
    sortBy?: string;
    sortDirection?: 'asc' | 'desc';
    search?: string;
  }
  
  class OrderService {
    private ordersCollection = collection(db, 'orders');
  
    async getOrders(options: OrderQueryOptions = {}): Promise<{ orders: Order[]; lastDoc: any }> {
      try {
        const constraints: QueryConstraint[] = [];
  
        if (options.status && options.status !== 'all') {
          constraints.push(where('status', '==', options.status));
        }
  
        if (options.customerId) {
          constraints.push(where('customer.id', '==', options.customerId));
        }
  
        if (options.sortBy) {
          constraints.push(orderBy(options.sortBy, options.sortDirection || 'desc'));
        } else {
          constraints.push(orderBy('createdAt', 'desc'));
        }
  
        if (options.limit) {
          constraints.push(firestoreLimit(options.limit));
        }
  
        if (options.startAfterDoc) {
          constraints.push(startAfter(options.startAfterDoc));
        }
  
        const q = query(this.ordersCollection, ...constraints);
        const snapshot = await getDocs(q);
  
        const orders = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        })) as Order[];
  
        if (options.search) {
          const searchLower = options.search.toLowerCase();
          return {
            orders: orders.filter(order =>
              order.orderNumber.toLowerCase().includes(searchLower) ||
              order.customer.name.toLowerCase().includes(searchLower) ||
              order.customer.email.toLowerCase().includes(searchLower)
            ),
            lastDoc: snapshot.docs[snapshot.docs.length - 1]
          };
        }
  
        return {
          orders,
          lastDoc: snapshot.docs[snapshot.docs.length - 1]
        };
      } catch (error) {
        console.error('Error fetching orders:', error);
        throw error;
      }
    }
  
    async getOrder(id: string): Promise<Order | null> {
      try {
        const docRef = doc(this.ordersCollection, id);
        const docSnap = await getDoc(docRef);
  
        if (!docSnap.exists()) return null;
  
        return {
          id: docSnap.id,
          ...docSnap.data()
        } as Order;
      } catch (error) {
        console.error('Error fetching order:', error);
        throw error;
      }
    }

    async getOrdersByCustomer(customerId: string): Promise<Order[]> {
        try {
          const q = query(this.ordersCollection, where('customer.id', '==', customerId));
          const snapshot = await getDocs(q);
          return snapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
          })) as Order[];
        } catch (error) {
          console.error('Error fetching orders by customer:', error);
          throw error;
        }
      }
  
    async updateOrderStatus(id: string, status: Order['status']): Promise<void> {
      try {
        const docRef = doc(this.ordersCollection, id);
        await updateDoc(docRef, {
          status,
          updatedAt: new Date()
        });
      } catch (error) {
        console.error('Error updating order status:', error);
        throw error;
      }
    }
  
    async getRecentOrders(limit: number = 5): Promise<Order[]> {
      try {
        const q = query(
          this.ordersCollection,
          orderBy('createdAt', 'desc'),
          firestoreLimit(limit)
        );
  
        const snapshot = await getDocs(q);
        return snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        })) as Order[];
      } catch (error) {
        console.error('Error fetching recent orders:', error);
        throw error;
      }
    }
  }
  
  export const orderService = new OrderService();